import type { PublicacaoPoliticaFinanceiro, RegimeTributario } from './comissao'

/**
 * Política de retenção da NF de comissão e a sugestão por nota (AN-PP5 §4, §6). Espelha
 * `dto/PoliticaRetencaoNfRequest`, `PoliticaRetencaoNfResponse`, `RetencoesSugeridasResponse` e
 * `service/SugestaoRetencao.Item`. Q73 (provisória): são retenções DO CONSULTOR — tributo próprio da
 * Originei nunca é deduzido da comissão.
 *
 * A sugestão sai pronta do backend (valores, total, líquido, completa/pendentes): a tela não calcula.
 */

/** Ordem da tela e do snapshot (`TributoRetencaoNf`). */
export const TRIBUTOS_RETENCAO = ['ISS', 'IR', 'INSS', 'PIS', 'COFINS', 'CSLL'] as const
export type TributoRetencao = (typeof TRIBUTOS_RETENCAO)[number]

/** Regime da regra: os da aprovação fiscal mais `TODOS` (vale para qualquer regime; perde para o exato). */
export type RegimeRetencao = RegimeTributario | 'TODOS'

/** Uma versão de regra (`GET/POST /v1/comissoes/politicas-retencao`). */
export interface PoliticaRetencao extends PublicacaoPoliticaFinanceiro {
  id: string
  tributo: TributoRetencao
  regimeTributario: RegimeRetencao
  /** Item LC 116 só com dígitos (o backend normaliza: 17.06 → 1706) ou `*` (todos). */
  codigoServico: string
  /** IBGE de 7 dígitos ou `*` (todos); só no ISS. */
  municipioIbge: string
  /** `false` = regra explícita de NÃO retenção. */
  retem: boolean
  /** FRAÇÃO (0,015 = 1,5%). A tela não exibe: usa `aliquotaPercentual`. */
  aliquota: number | null
  /** O mesmo número já em percentual, pronto do backend. */
  aliquotaPercentual: number | null
  base: string
  dispensaAbaixoDe: number | null
  versao: number
  vigenciaInicio: string
  /** Exclusivo; nulo = aberta. */
  vigenciaFim: string | null
  vigenteHoje: boolean
  origem: string
  /** A tela não exibe. */
  criadoPor?: string | null
  criadoEm: string
}

/** Área dona aceita na proposta de retenção (padrão do backend: FINANCEIRO). */
export type OwnerRetencao = 'FINANCEIRO' | 'JURIDICO'

/**
 * `POST /v1/comissoes/politicas-retencao` — PROPÕE (202, PENDENTE_APROVACAO); só vale (e só fecha a aberta da mesma
 * chave) quando OUTRA pessoa FINANCEIRO/ADMIN aprovar.
 */
export interface PoliticaRetencaoRequest {
  tributo: TributoRetencao
  regimeTributario: RegimeRetencao
  /** Vazio ou `*` = todos; com ou sem pontuação (gravado só com dígitos). */
  codigoServico: string | null
  /** Vazio ou `*` = todos; só no ISS. */
  municipioIbge: string | null
  retem: boolean
  /**
   * FIX-FIN A1: alíquota em PERCENTUAL (0,65 = 0,65%), até 4 casas; o backend converte para fração. Obrigatória se
   * e só se `retem`; acima do teto do tributo → 422 `ALIQUOTA_ACIMA_DO_TETO`.
   */
  aliquotaPercentual: number | null
  /** Fração (contrato antigo, compatibilidade). A tela não envia; com as duas divergentes → 422 `ALIQUOTA_DIVERGENTE`. */
  aliquota?: number | null
  dispensaAbaixoDe: number | null
  /** `YYYY-MM-DD` */
  vigenciaInicio: string
  /** Fundamento ("Parecer Jurídico 2026-xx", "IN RFB …"). */
  origem: string
  /** Ausente = FINANCEIRO. */
  owner?: OwnerRetencao | null
}

/** Situação de cada tributo na sugestão (`SugestaoRetencao.Situacao`). */
export type SituacaoRetencaoSugerida =
  | 'SUGERIDA'
  | 'NAO_RETEM'
  | 'DISPENSADA_VALOR_MINIMO'
  | 'SEM_POLITICA'
  | 'MUNICIPIO_NAO_INFORMADO'

export interface RetencaoSugeridaItem {
  tributo: TributoRetencao
  situacao: SituacaoRetencaoSugerida
  /** Fração; a tela não exibe (o texto pronto está em `regraAplicada`). */
  aliquota: number | null
  base: string
  valorBase: number | null
  /** Nulo em SEM_POLITICA / MUNICIPIO_NAO_INFORMADO. */
  valorSugerido: number | null
  politicaId: string | null
  versao: number | null
  /** Texto pronto do backend: tributo, versão, chave, alíquota em %, dispensa. */
  regraAplicada: string
}

/** `GET /v1/comissoes/notas-fiscais/{id}/retencoes-sugeridas?regime=&municipioIbge=&codigoServico=` */
export interface RetencoesSugeridas {
  notaId: string
  conferenciaVersao: number
  dataEmissao: string
  valorServicos: number
  regimeTributario: RegimeRetencao
  /** PARAMETRO | LEITURA_ARQUIVO */
  regimeOrigem: string
  codigoServico: string | null
  codigoServicoOrigem: string | null
  municipioIbge: string | null
  municipioOrigem: string | null
  itens: RetencaoSugeridaItem[]
  totalSugerido: number
  valorLiquidoSugerido: number
  /** Nenhum tributo sem sugestão. */
  completa: boolean
  /** Tributos sem sugestão (SEM_POLITICA / MUNICIPIO_NAO_INFORMADO). */
  pendentes: TributoRetencao[]
  /** Chave de produção ligada: a aprovação recusa (409) enquanto houver pendente. */
  exigePoliticaCompleta: boolean
}

/** `data.divergencias` do 422 `RETENCAO_DIVERGE_SUGESTAO_SEM_JUSTIFICATIVA` (valores em texto, do backend). */
export interface DivergenciaRetencao {
  tributo: TributoRetencao
  sugerido: string
  informado: string
}
